import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { getPublicSharedTemplate } from '../../api/shareLinkApi';
import { Button } from '../../components/ui/Button';
import { Callout } from '../../components/ui/Panel';
import { Spinner } from '../../components/ui/Stat';
import { ROUTES } from '../../lib/config';
import { formatDateTime } from '../../lib/format';
import { keys } from '../../lib/queryKeys';
import { useAuth } from '../../providers/AuthProvider';
import { buildPlainTextPreview, buildPreviewDocument, PREVIEW_SANDBOX } from '../dashboard/htmlPreview';
import { SHAREABLE_FIELD_LABELS, queuePendingSharedTemplate } from './shareTemplate';

/** Fields shown in the settings list, in composer order. Message has its own preview. */
const SUMMARY_FIELDS = [
  'smtpHost',
  'smtpPort',
  'smtpUser',
  'fromName',
  'fromEmail',
  'subject',
  'messageType',
  'contentTransferEncoding',
  'returnPath',
  'customMessageId',
  'inboxPatternId',
  'limit',
  'intervalSeconds',
  'limitToSend',
  'autoImapTest'
];

function displayValue(field, value) {
  if (field === 'autoImapTest') return value ? 'On' : 'Off';
  if (field === 'inboxPatternId') return value ? 'Custom pattern' : 'Default';
  return String(value ?? '').trim() || '—';
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-surface-sunken px-4 py-8">
      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        <div className="flex items-center gap-3">
          <img src="/logo.svg" alt="" className="h-9 w-9 rounded-full border border-line shadow-sm" />
          <span className="text-lg font-bold text-ink-900">Opterite</span>
          <span className="text-sm text-muted">· Shared campaign form</span>
        </div>
        {children}
      </div>
    </div>
  );
}

const ERROR_COPY = {
  NOT_FOUND: { title: 'Link not found', body: 'This share link does not exist. Check that you copied the whole URL.' },
  EXPIRED: { title: 'This link has expired', body: 'Ask the person who shared it for a new link.' },
  REVOKED: { title: 'This link has been revoked', body: 'The owner has turned this link off. Ask them for a new one.' }
};

/**
 * Public, read-only view of a shared campaign form.
 *
 * Viewing needs no login. "Open in composer" parks the token and goes to the dashboard
 * (via login if needed), where the values are copied into the viewer's own draft. This
 * page never sends, enqueues or writes anything beyond the server's open counter.
 */
export function SharedTemplatePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const query = useQuery({
    queryKey: keys.sharedTemplate(token),
    queryFn: ({ signal }) => getPublicSharedTemplate(token, signal),
    retry: (count, err) => !(err?.status >= 400 && err?.status < 500) && count < 2,
    staleTime: 60_000,
    refetchOnWindowFocus: false
  });

  const template = query.data;
  const config = template?.config ?? {};
  const isHtml = config.messageType === 'HTML';

  const previewDocument = useMemo(() => {
    if (!template) return '';
    return isHtml ? buildPreviewDocument(config.message ?? '') : buildPlainTextPreview(config.message ?? '');
  }, [template, isHtml, config.message]);

  function openInComposer() {
    queuePendingSharedTemplate(token);
    navigate(ROUTES.dashboard);
  }

  if (query.isLoading) {
    return (
      <Shell>
        <div className="flex justify-center py-16">
          <Spinner label="Loading shared form…" />
        </div>
      </Shell>
    );
  }

  if (query.error) {
    const copy = ERROR_COPY[query.error.code] ?? {
      title: 'Could not load this link',
      body: query.error.message
    };
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 rounded-lg border border-line bg-surface px-6 py-12 text-center shadow-xs">
          <i className="fa-solid fa-link-slash text-3xl text-ink-300" aria-hidden="true" />
          <h1 className="text-xl font-bold text-ink-900">{copy.title}</h1>
          <p className="max-w-md text-base text-muted">{copy.body}</p>
          <Link to={ROUTES.dashboard} className="no-underline">
            <Button variant="secondary" icon="fa-house">
              Go to Opterite
            </Button>
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex flex-col gap-4 rounded-lg border border-line bg-surface p-5 shadow-xs">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-ink-900">{template.name || config.subject || 'Shared campaign form'}</h1>
            <p className="text-sm text-muted">
              {template.ownerName ? `Shared by ${template.ownerName} · ` : ''}
              Created {formatDateTime(template.createdAt)}
              {template.expiresAt ? ` · Expires ${formatDateTime(template.expiresAt)}` : ''}
            </p>
          </div>
          <Button variant="primary" icon="fa-pen-to-square" onClick={openInComposer}>
            Open in composer
          </Button>
        </div>

        <Callout tone="info" icon="fa-circle-info">
          Opening this form in the composer gives you your own copy to edit. Your changes never affect the
          original link or the person who shared it, and nothing is sent until you click Send yourself.
          {isAuthenticated ? '' : ' You will be asked to sign in first.'} Recipients and the SMTP password are
          not included; add your own.
        </Callout>

        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          {SUMMARY_FIELDS.filter((field) => config[field] !== undefined).map((field) => (
            <div key={field} className="flex min-w-0 flex-col">
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{SHAREABLE_FIELD_LABELS[field]}</dt>
              <dd className="break-words text-base text-ink-800">{displayValue(field, config[field])}</dd>
            </div>
          ))}
        </dl>

        {String(config.customHeaders ?? '').trim() ? (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">Custom Headers</span>
            <pre className="max-h-48 overflow-auto rounded-md border border-line bg-ink-25 p-3 font-mono text-sm whitespace-pre-wrap">
              {config.customHeaders}
            </pre>
          </div>
        ) : null}

        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted">
            Message ({isHtml ? 'HTML' : 'plain text'})
          </span>
          {String(config.message ?? '').trim() ? (
            <div className="overflow-hidden rounded-md border border-line bg-white shadow-xs">
              <iframe
                title="Shared message preview"
                srcDoc={previewDocument}
                // No allow-scripts: same security boundary as MessagePreviewModal.
                sandbox={PREVIEW_SANDBOX}
                referrerPolicy="no-referrer"
                className="block h-[60vh] w-full border-0 bg-white"
              />
            </div>
          ) : (
            <p className="text-base text-muted">No message body.</p>
          )}
        </div>
      </div>
    </Shell>
  );
}
